import {FormControl} from 'platform/components';

import {RefObject, useCallback, useEffect} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  AccountingDocumentRecalculateApiArg,
  AccountingDocumentRecalculateItemRes,
  useInvoicingDocumentRecalculateMutation,
  useTaxDocumentCalculationV2Mutation,
} from '@dms/api/finance';
import {PaymentType, VatCheckTotalsDefinitionDocumentTypeEnum} from '@dms/api/shared';
import {handleApiError, useGetCurrentBranch} from '@dms/shared';

import {Nullish} from 'shared/web';

import {useAccountingCalculationCache} from '../../../context/useAccountingCalculationCache';
import {AccountingDocumentFormValues} from '../../../types';
import {useGetInvoiceItemsForRecalculation} from '../hooks/useGetInvoiceItemsForRecalculation';
import {WithCorrectiveTaxDocumentArgs} from '../types/WithCorrectiveTaxDocumentArgs';
import {isValidIndex} from '../utils/isValidIndex';
import {useGetInvoiceCurrency} from './useGetInvoiceCurrency';
import {UpdateRowType, useRerenderRows} from './useRerenderRows';

type RecalculateInvoiceItemsArgs = WithCorrectiveTaxDocumentArgs<{
  control: FormControl<AccountingDocumentFormValues>;
  formApi: UseFormReturn<AccountingDocumentFormValues>;
  listItemType: VatCheckTotalsDefinitionDocumentTypeEnum;
  shouldCallOnInitialization?: boolean;
  pricePerUnitEditVersionRef?: RefObject<Record<string, number>>;
}>;

type RecalculateSummaryArgs =
  | (Omit<AccountingDocumentRecalculateApiArg, 'items'> & {
      items: AccountingDocumentRecalculateItemRes[];
    })
  | Nullish;

export const useRecalculateInvoiceItems = (props: RecalculateInvoiceItemsArgs) => {
  const {activeBranchId} = useGetCurrentBranch();
  const {data: tenant} = useGetTenantQuery();
  const {taxDocumentCalculationCacheKey, accountingDocumentCalculationCacheKey} =
    useAccountingCalculationCache();

  const [currency] = useGetInvoiceCurrency(props.control, props.listItemType);
  const [mapInvoiceItemsForRecalculation] = useGetInvoiceItemsForRecalculation(
    props.isCorrectiveTaxDocument ?? false,
    currency
  );

  const [calculateTaxDocument] = useInvoicingDocumentRecalculateMutation({
    fixedCacheKey: accountingDocumentCalculationCacheKey,
  });

  const [recalculateSummary] = useTaxDocumentCalculationV2Mutation({
    fixedCacheKey: taxDocumentCalculationCacheKey,
  });

  const {updateDiscount, updateMargin, updateStandardItem, updateAllItems, resetInvalidDiscounts} =
    useRerenderRows(props.formApi, props.pricePerUnitEditVersionRef);

  const recalculateInvoiceSummary = useCallback(
    (recalculateProps: RecalculateSummaryArgs) => {
      if (isNil(recalculateProps)) {
        return;
      }

      const {items, dueDate, ...args} = recalculateProps;

      const invoiceItems = items.map((item) => {
        if (item.type === 'discount') {
          return {
            isUnitPriceWithVat: item.isUnitPriceWithVat,
            quantity: '1',
            reference: item.itemId,
            unitPrice: item.unitPrice.toString() ?? '0',
            vatType: item.vatType,
            relatedItemId: item.relatedItemId ?? undefined,
          };
        }

        return {
          isUnitPriceWithVat: item.isUnitPriceWithVat,
          quantity: item.quantity ?? '0',
          reference: item.itemId,
          unitPrice: item.unitPrice ?? '0',
          vatType: item.vatType,
          relatedItemId: item.relatedItemId ?? undefined,
        };
      });

      return recalculateSummary({
        taxDocumentCalculationRequestBody: {
          ...args,
          taxableSupplyDate: dueDate,
          items: invoiceItems,
          relatedDocumentForCalculation: props.relatedDocumentForCalculation ?? null,
        },
      })
        .unwrap()
        .catch(handleApiError);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recalculateSummary]
  );

  const recalculate = useCallback(
    (itemId?: string) => {
      if (isNil(activeBranchId) || isNil(tenant)) {
        return;
      }

      const {
        invoiceItems,
        dateOfTaxableSupply,
        paymentInfo,
        isExchangeRateRatioEnabled,
        exchangeRateRatio,
      } = props.formApi.getValues();

      const items = mapInvoiceItemsForRecalculation(invoiceItems);
      const hasItemWithNonZeroQuantity = items.some((item) => parseFloat(item.quantity) !== 0);
      // check if all necessary invoice fields are present
      if (
        isNilOrEmpty(dateOfTaxableSupply) ||
        isNilOrEmpty(paymentInfo?.paymentMethod) ||
        isNilOrEmpty(items) ||
        !hasItemWithNonZeroQuantity
      ) {
        return;
      }

      // this is necessary due to difference between calc EP and invoiceApiResponse
      const paymentType =
        paymentInfo.paymentMethod === 'CARD'
          ? 'payment_card'
          : (paymentInfo.paymentMethod.toLowerCase() as PaymentType);

      const exchangeRatio =
        isExchangeRateRatioEnabled &&
        isNotNilOrEmpty(exchangeRateRatio?.amount) &&
        isNotNilOrEmpty(exchangeRateRatio?.ratio)
          ? {
              ratio: exchangeRateRatio?.ratio?.toString() ?? '',
              amount: parseInt(exchangeRateRatio?.amount ?? ''),
              currency: exchangeRateRatio?.currency ?? '',
            }
          : null;

      const recalculateInvoiceDocumentBody = {
        vatCheckTotalsDocumentType: props.listItemType,
        dueDate: dateOfTaxableSupply as string,
        country: tenant.country,
        exchangeRateRatio: exchangeRatio,
        branchId: activeBranchId,
        paymentType,
        currency,
        items,
      };

      const capturedEditVersions = {...(props.pricePerUnitEditVersionRef?.current ?? {})};

      calculateTaxDocument(recalculateInvoiceDocumentBody)
        .unwrap()
        .then((data) => {
          resetInvalidDiscounts({invoiceItems});

          if (isNil(itemId)) {
            updateAllItems({invoiceItems, items: data.items});
            return {...recalculateInvoiceDocumentBody, items: data.items};
          }

          const updatedItemIndex = invoiceItems.findIndex((item) => item.itemId === itemId);
          const updatedItem = data.items.find((item) => item.itemId === itemId);

          if (isNil(updatedItem) || isNil(itemId) || !isValidIndex(updatedItemIndex)) {
            return {...recalculateInvoiceDocumentBody, items: data.items};
          }

          const updateRowProps: UpdateRowType = {
            invoiceItems,
            updatedItem,
            updatedItemIndex,
            items: data.items,
            capturedEditVersions,
          };

          if (updatedItem.type === 'discount') {
            updateDiscount(updateRowProps);
          }
          if (updatedItem.type === 'margin') {
            updateMargin(updateRowProps);
          }
          if (updatedItem.type === 'standard') {
            updateStandardItem(updateRowProps);
          }

          return {...recalculateInvoiceDocumentBody, items: data.items};
        })
        .then(recalculateInvoiceSummary)
        .catch((error: Error) => handleApiError(error, {silent: true}));
    },
    [
      activeBranchId,
      calculateTaxDocument,
      currency,
      props.formApi,
      props.listItemType,
      mapInvoiceItemsForRecalculation,
      recalculateInvoiceSummary,
      resetInvalidDiscounts,
      tenant,
      updateAllItems,
      updateDiscount,
      updateMargin,
      updateStandardItem,
    ]
  );

  useEffect(() => {
    if (props.shouldCallOnInitialization) {
      recalculate();
    }
  }, [props.shouldCallOnInitialization, recalculate]);

  return [recalculate];
};
